import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';

const Header = () => {
  const [logo, setLogo] = useState(null);
  const [logoText, setLogoText] = useState('Dolphin Institute');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const location = useLocation();

  const navLinks = [
    { to: '/', icon: 'fa-home', label: 'Home' },
    { to: '/courses', icon: 'fa-graduation-cap', label: 'Courses' },
    { to: '/departments', icon: 'fa-building', label: 'Departments' },
    { to: '/news', icon: 'fa-newspaper', label: 'News' },
    { to: '/faculty', icon: 'fa-chalkboard-user', label: 'Faculty' },
    { to: '/gallery', icon: 'fa-image', label: 'Gallery' },
    { to: '/contact', icon: 'fa-envelope', label: 'Contact' }
  ];

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'college_info');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const settings = settingsSnap.data();
          if (settings.logoUrl) setLogo(settings.logoUrl);
          if (settings.collegeName) setLogoText(settings.collegeName);
        }
      } catch (error) {
        console.error('Settings error:', error);
      }
    };
    loadSettings();
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header style={{
      background: 'rgba(255, 255, 255, 0.45)',
      backdropFilter: 'blur(20px) saturate(140%)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.6)',
      position: 'sticky',
      top: 0,
      zIndex: 1000
    }}>
      <div className="container">
        <nav style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 0',
          flexWrap: 'wrap',
          gap: '15px',
          position: 'relative'
        }}>
          {/* Logo */}
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
            flexShrink: 0
          }}>
            {logo ? (
              <img src={logo} alt="Logo" style={{
                height: isMobile ? '36px' : '44px',
                width: 'auto',
                objectFit: 'contain',
                background: 'rgba(255,255,255,0.5)',
                borderRadius: '8px',
                padding: '4px'
              }} />
            ) : (
              <span style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: isMobile ? '0.85rem' : '1rem',
                fontWeight: 700,
                background: 'rgba(255,255,255,0.5)',
                padding: '6px 12px',
                borderRadius: '8px'
              }}>{logoText}</span>
            )}
          </Link>

          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              <ul style={{
                display: 'flex',
                listStyle: 'none',
                gap: '6px',
                background: 'rgba(255,255,255,0.3)',
                padding: '5px',
                borderRadius: '12px',
                flexWrap: 'wrap'
              }}>
                {navLinks.map(link => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        color: location.pathname === link.to ? 'white' : 'var(--text-secondary)',
                        textDecoration: 'none',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        transition: 'all 0.3s ease',
                        background: location.pathname === link.to ? 'linear-gradient(135deg, var(--clr-blue), #0284c7)' : 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (location.pathname !== link.to) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, var(--clr-blue), #0284c7)';
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (location.pathname !== link.to) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'var(--text-secondary)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      <i className={`fas ${link.icon}`}></i> {link.label}
                    </Link>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => window.open('https://dimst.in/dim/site/login', '_blank')}
                style={{
                  background: 'linear-gradient(135deg, var(--clr-amber), #d97706)',
                  border: 'none',
                  padding: '8px 20px',
                  borderRadius: '100px',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(245,158,11,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <i className="fas fa-chalkboard"></i> Teacher Login
              </button>
            </>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={toggleMenu}
              style={{
                background: 'rgba(255,255,255,0.3)',
                border: '1px solid rgba(255,255,255,0.6)',
                padding: '8px 12px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1.2rem',
                color: 'var(--text-primary)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease'
              }}
            >
              <i className={`fas ${isMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
            </button>
          )}
        </nav>

        {/* Mobile Menu */}
        {isMobile && isMenuOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px) saturate(140%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.6)',
            padding: '16px 0',
            zIndex: 1000,
            animation: 'slideDown 0.3s ease'
          }}>
            <ul style={{
              listStyle: 'none',
              padding: '0 16px',
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {navLinks.map(link => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      color: location.pathname === link.to ? 'white' : 'var(--text-secondary)',
                      textDecoration: 'none',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      transition: 'all 0.3s ease',
                      background: location.pathname === link.to ? 'linear-gradient(135deg, var(--clr-blue), #0284c7)' : 'transparent',
                      width: '100%'
                    }}
                  >
                    <i className={`fas ${link.icon}`} style={{ width: '20px' }}></i> {link.label}
                  </Link>
                </li>
              ))}
              <li style={{ marginTop: '8px' }}>
                <button
                  onClick={() => {
                    window.open('https://dimst.in/dim/site/login', '_blank');
                    setIsMenuOpen(false);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, var(--clr-amber), #d97706)',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '100px',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    justifyContent: 'center'
                  }}
                >
                  <i className="fas fa-chalkboard"></i> Teacher Login
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Add CSS animation */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </header>
  );
};

export default Header;